import '@/utils/ensure-lingui-locale'
import '@/scripts/wdyr'
import 'react-querybuilder/dist/query-builder.scss'
import '@/styles/global.css'
import '@/styles/loader.scss'
import '@/styles/cmdk.scss'
import 'primereact/resources/themes/lara-light-indigo/theme.css'
import 'primereact/resources/primereact.css'

import { SessionContextProvider } from '@dolfin/hooks/supabase'
import { ReactElement, ReactNode, useEffect, useRef, useState } from 'react'

import 'flowbite'
import Head from 'next/head'
import type { AppProps } from 'next/app'
import type { NextPage } from 'next'
import { i18n } from '@lingui/core'
import { I18nProvider } from '@lingui/react'
import { Router, useRouter } from 'next/router'
import { QueryClient, QueryClientProvider } from 'react-query'
import ClientOnly from '@dolfin/ui/utils/renderers'
import { Toaster } from '@/src/components'
import NextNProgress from 'nextjs-progressbar'
import { ErrorBoundary, Provider as RollbarProvider } from '@rollbar/react'
import Intercom, {
  shutdown as shutdownIntercom
} from '@intercom/messenger-js-sdk'
import { getOrgRole } from '@dolfin/utils/roles'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import ErrorPage from '@/src/error-page'
import { getDefaultMessagesSync } from '@/utils/i18n'
import { createClient } from '@/utils/supabase/client'
import { WebDrawerAnalyticsProvider } from '@/src/components/WebDrawerAnalyticsProvider'
import { setPostHogOrganizationContext } from '@/lib/posthog-organization-context'

const queryClient = new QueryClient()
const hasCatalogMessages = (messages?: Record<string, unknown>) =>
  Object.keys(messages ?? {}).length > 0

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const [supabaseClient] = useState(() => createClient())
  const posthogInitialized = useRef(false)

  const getLayout = Component.getLayout ?? ((page: ReactElement) => page)

  const router = useRouter()
  const locale =
    pageProps.locale ?? router.locale ?? router.defaultLocale ?? 'en'
  const isImpersonating = pageProps?.isImpersonating === true

  const isClient = typeof window !== 'undefined'

  // Use default (en) messages when translation is missing (e.g. during Next.js build when getServerSideProps hasn't run)
  const messages = pageProps.translation ?? getDefaultMessagesSync()

  if (!isClient && locale !== i18n.locale) {
    // there is single instance of i18n on the server
    i18n.loadAndActivate({ locale, messages })
  }
  if (
    isClient &&
    (!i18n.locale ||
      locale !== i18n.locale ||
      !hasCatalogMessages(i18n.messages as Record<string, unknown>))
  ) {
    // Always recover when client bootstrap has locale but no catalog loaded
    i18n.loadAndActivate({ locale, messages })
  }

  useEffect(() => {
    if (isImpersonating) {
      shutdownIntercom()
      return
    }

    if (pageProps?.profile?.email) {
      Intercom({
        app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID!,
        region: 'eu',
        user_id: pageProps.profile.id,
        name: pageProps.profile.name,
        email: pageProps.profile.email,
        user_hash: pageProps.intercom_hash,
        'User Type': !!pageProps.selectedOrganization?.id
          ? getOrgRole(pageProps.profile, pageProps.selectedOrganization.id)
          : undefined
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isImpersonating,
    pageProps?.profile?.email,
    pageProps?.profile?.id,
    pageProps?.profile?.name,
    pageProps?.intercom_hash,
    pageProps?.selectedOrganization?.id
  ])

  const oldUrlRef = useRef('')

  useEffect(() => {
    const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY
    const handleRouteChange = () => posthog?.capture('$pageview')

    const handleRouteChangeStart = () =>
      posthog?.capture('$pageleave', {
        $current_url: oldUrlRef.current
      })

    if (isImpersonating) {
      posthog.reset()
      posthog.opt_out_capturing()
      Router.events.off('routeChangeComplete', handleRouteChange)
      Router.events.off('routeChangeStart', handleRouteChangeStart)
      return
    }

    if (!posthogKey) {
      return
    }

    if (!posthogInitialized.current) {
      posthog.init(posthogKey, {
        api_host:
          process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
        person_profiles: 'identified_only',
        // Enable debug mode in development
        loaded: ph => {
          if (process.env.NODE_ENV === 'development') {
            ph.debug()
          }
        }
      })

      posthogInitialized.current = true
    }

    posthog.opt_in_capturing()

    Router.events.on('routeChangeComplete', handleRouteChange)
    Router.events.on('routeChangeStart', handleRouteChangeStart)
    handleRouteChange()

    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange)
      Router.events.off('routeChangeStart', handleRouteChangeStart)
    }
  }, [isImpersonating])

  useEffect(() => {
    if (
      isImpersonating ||
      !posthogInitialized.current ||
      !pageProps?.profile?.email ||
      !pageProps?.profile?.id
    ) {
      return
    }

    posthog.identify(String(pageProps.profile.id), {
      email: pageProps.profile.email,
      name: pageProps.profile.name ?? '',
      organizationId: pageProps.selectedOrganization?.id ?? null,
      organization_id: pageProps.selectedOrganization?.id ?? null,
      organization_name: pageProps.selectedOrganization?.name ?? null
    })
    setPostHogOrganizationContext(posthog, {
      email: pageProps.profile.email,
      organizationId: pageProps.selectedOrganization?.id,
      organizationName: pageProps.selectedOrganization?.name
    })
    posthog.reloadFeatureFlags()
  }, [
    isImpersonating,
    pageProps?.profile?.email,
    pageProps?.profile?.id,
    pageProps?.profile?.name,
    pageProps?.selectedOrganization?.id,
    pageProps?.selectedOrganization?.name
  ])

  useEffect(() => {
    const localeDidChange = locale !== i18n.locale
    const missingCatalog = !hasCatalogMessages(
      i18n.messages as Record<string, unknown>
    )
    if (localeDidChange || missingCatalog) {
      i18n.loadAndActivate({
        locale,
        messages: pageProps.translation ?? getDefaultMessagesSync()
      })
    }
  }, [locale, pageProps.translation])

  const rollbarConfig = {
    accessToken: process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN,
    captureUncaught: true,
    captureUnhandledRejections: true,
    payload: {
      environment: process.env.NEXT_PUBLIC_VERCEL_ENV,
      client: {
        javascript: {
          code_version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
          source_map_enabled: true
        }
      }
    }
  }

  return (
    <>
      <Head>
        <meta charSet='UTF-8' />
        <meta httpEquiv='X-UA-Compatible' content='IE=edge' />
        <meta name='viewport' content='width=device-width, initial-scale=1.0' />
        <title>Dolfin | Automating sales commissions</title>
        {/* Meta SEO */}
        <meta name='title' content='Dolfin - Calculate Commissions' />
        <meta
          name='description'
          content='Optimize your earnings and improve your bottom line with our powerful sales commission calculator. Say goodbye to manual calculations and guesswork - our streamlined SaaS solution makes it easy to accurately calculate commissions for improved revenue and stronger financial performance. Experience hassle-free commission tracking and unlock your full earning potential with our intuitive and precise commission calculation software'
        />
        <meta name='robots' content='index, follow' />
        <meta httpEquiv='Content-Type' content='text/html; charset=utf-8' />
        <meta name='language' content='English' />
        <meta name='author' content='Dolfin Team' />
        {/* Social media share */}
        <meta
          property='og:title'
          content='Dolfin - Streamline your commission calculations for a better bottom line.'
        />
        <meta property='og:site_name' content='Dolfin' />
        <meta property='og:url' content='https://dolfin.es/' />
        <meta
          property='og:description'
          content='Optimize your earnings and improve your bottom line with our powerful sales commission calculator. Say goodbye to manual calculations and guesswork - our streamlined SaaS solution makes it easy to accurately calculate commissions for improved revenue and stronger financial performance. Experience hassle-free commission tracking and unlock your full earning potential with our intuitive and precise commission calculation software'
        />
        <meta property='og:type' content='' />
        <meta
          property='og:image'
          content='/dolfin_logo_only_icon_blue_bg.png'
        />
        <meta name='theme-color' content='#030712' />

        {/* Favicon */}
        <link
          rel='apple-touch-icon'
          sizes='180x180'
          href='/apple-touch-icon.png'
        />
        <link
          rel='icon'
          type='image/png'
          sizes='32x32'
          href='/favicon-32x32.png'
        />
        <link
          rel='icon'
          type='image/png'
          sizes='16x16'
          href='/favicon-16x16.png'
        />
        <link rel='manifest' href='/site.webmanifest' />
      </Head>

      <NextNProgress />

      <PostHogProvider client={posthog}>
        <WebDrawerAnalyticsProvider
          isImpersonating={isImpersonating}
          selectedOrganization={pageProps.selectedOrganization}>
          <RollbarProvider config={rollbarConfig}>
            <SessionContextProvider supabaseClient={supabaseClient}>
              <QueryClientProvider client={queryClient}>
                <I18nProvider i18n={i18n}>
                  {getLayout(
                    <ErrorBoundary fallbackUI={ErrorPage}>
                      <Component {...pageProps} />
                    </ErrorBoundary>
                  )}
                  {/*<ReactQueryDevtools initialIsOpen={false} />*/}
                  <div id={'modal-container'} />

                  <ClientOnly>
                    <Toaster />
                  </ClientOnly>
                </I18nProvider>
              </QueryClientProvider>
            </SessionContextProvider>
          </RollbarProvider>
        </WebDrawerAnalyticsProvider>
      </PostHogProvider>
    </>
  )
}

export default MyApp
